import React, { useEffect, useState } from "react";
import AccentReadOnlyCheckbox from "../../../../../../../common/FormFields/AccentReadOnlyCheckbox";
import HeadingComponent from "../../HeadingComponent";
import HtmlOrPlainShortDescription from "../../HtmlOrPlainShortDescription";
import CanvasPageRenderer from "../../../../../../Canvas/components/CanvasPageRenderer";
import { parseAuditedChildOrdinalFromHeading } from "../../../../utils/sectionDisplayOrdinal";

/**
 * Extract page image URLs from content (for fallback when no full JSON).
 * API format: content.pages = ["url1", "url2"]
 * Snapshot format: content.ctm_data = [{ canva_snap_url: "..." }]
 */
function getPagesFromContent(content) {
  if (!content) return [];
  if (Array.isArray(content.pages) && content.pages.length > 0) {
    return content.pages.map((p) => (typeof p === "string" ? p : p?.canva_snap_url)).filter(Boolean);
  }
  const ctmData = content.ctm_data || content.ctmData || [];
  const fromCtm = ctmData.map((c) => c?.canva_snap_url).filter(Boolean);
  if (fromCtm.length > 0) return fromCtm;
  return [];
}

const Canvas = ({
  content,
  globalCount,
  heading,
  treepath,
  showCheckbox,
  isChecked,
  onCheckboxChange,
  disabled = false,
  readOnlyCheckboxTitleMatch = null,
}) => {
  const auditBarCanvasN =
    readOnlyCheckboxTitleMatch?.kind === "canvas" &&
    readOnlyCheckboxTitleMatch.n != null &&
    !Number.isNaN(Number(readOnlyCheckboxTitleMatch.n))
      ? Number(readOnlyCheckboxTitleMatch.n)
      : null;
  const displayHeading = (() => {
    if (auditBarCanvasN != null && isChecked) {
      return `Canvas ${auditBarCanvasN}`;
    }
    if (typeof heading === "string" && /^Canvas\s+\d+/i.test(heading.trim())) {
      return heading.trim();
    }
    if (globalCount != null && !Number.isNaN(Number(globalCount))) {
      return `Canvas ${globalCount}`;
    }
    if (typeof heading === "string" && heading.trim()) return heading.trim();
    return "Canvas";
  })();
  const ordinalForHighlight =
    auditBarCanvasN != null && isChecked
      ? auditBarCanvasN
      : parseAuditedChildOrdinalFromHeading(heading, "canvas") ??
        (globalCount != null && !Number.isNaN(Number(globalCount)) ? Number(globalCount) : null);
  const emphasizeReadOnlyCheckbox =
    disabled &&
    readOnlyCheckboxTitleMatch?.kind === "canvas" &&
    ordinalForHighlight != null &&
    Number(readOnlyCheckboxTitleMatch.n) === Number(ordinalForHighlight) &&
    (isChecked || false);

  const [canvasJsonData, setCanvasJsonData] = useState(null);
  const [fetchDone, setFetchDone] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const staticPages = getPagesFromContent(content);
  const jsonFileUrl = content?.canvas_json_data_file;

  useEffect(() => {
    if (staticPages.length > 0 || !jsonFileUrl) {
      setCanvasJsonData(null);
      setFetchDone(true);
      setFetchError(null);
      return;
    }
    setFetchDone(false);
    setFetchError(null);
    let cancelled = false;
    fetch(jsonFileUrl, { mode: "cors", credentials: "omit", cache: "no-cache" })
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load canvas: ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        setFetchDone(true);
        if (!data) {
          setCanvasJsonData(null);
          return;
        }
        if (!cancelled) setCanvasJsonData(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setFetchDone(true);
          setCanvasJsonData(null);
          setFetchError(err?.message || "Failed to load canvas");
        }
      });
    return () => { cancelled = true; };
  }, [jsonFileUrl, staticPages.length]);

  const hasFullCanvas = canvasJsonData && (
    Array.isArray(canvasJsonData) ? canvasJsonData.length > 0 : (canvasJsonData?.pages?.length > 0 || canvasJsonData?.results?.pages?.length > 0)
  );

  return (
    <div
      style={{ marginTop: "20px", marginBottom: "20px" }}
      className="header-spacing"
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
      >
        {showCheckbox &&
          (disabled ? (
            <AccentReadOnlyCheckbox
              readOnlyVisual
              emphasizeChecked={emphasizeReadOnlyCheckbox}
              checked={isChecked || false}
            />
          ) : (
            <input
              type="checkbox"
              checked={isChecked || false}
              onChange={onCheckboxChange}
              style={{
                width: "18px",
                height: "18px",
                cursor: "pointer",
                flexShrink: 0,
              }}
            />
          ))}
        <div style={{ flex: 1, width: "100%", minWidth: 0 }}>
          <HeadingComponent heading={displayHeading} treepath={treepath} />
        </div>
      </div>
      {content?.short_description != null && content.short_description !== "" && (
        <HtmlOrPlainShortDescription value={content.short_description} />
      )}
      {jsonFileUrl && !fetchDone && (
        <div style={{ marginTop: "10px", textAlign: "center", color: "#666" }}>Loading canvas…</div>
      )}
      {fetchError && (
        <div style={{ marginTop: "10px", textAlign: "center", color: "#c00" }}>{fetchError}</div>
      )}
      {hasFullCanvas && (
        <div style={{ marginTop: "16px" }}>
          <CanvasPageRenderer
            canvasData={canvasJsonData}
            width={Math.min(typeof window !== "undefined" ? window.innerWidth * 0.5 : 500, 600)}
            showSnapshots={false}
          />
        </div>
      )}
      {!hasFullCanvas && fetchDone && staticPages.length > 0 && (
        staticPages.map((url, index) => (
          <img
            key={index}
            src={url}
            alt={`Canvas snapshot ${index + 1}`}
            style={{
              maxWidth: "600px",
              width: "100%",
              height: "auto",
              marginTop: "10px",
              display: "block",
            }}
          />
        ))
      )}
    </div>
  );
};

export default Canvas;
